/** @fileoverview Implements tenant-scoped Fabrik chat API calls through Platform auth. */

import type { Conversation, ConversationPage, MessagePage } from '../types/chat';
import { json } from './platformAuth';

const conversations = '/app/fabrik/assistant-conversations';
const conversationPath = (id: string) => `${conversations}/${encodeURIComponent(id)}`;
export const assistantConversationPath = conversationPath;
export const assistantMessagesPath = (id: string) => `${conversationPath(id)}/assistant-messages`;

export const listConversations = (
  tenantId: string,
  offset = 0,
  signal?: AbortSignal,
  appId?: string
) => {
  const params = new URLSearchParams({ limit: '30', offset: String(offset) });
  if (appId) params.set('app_id', appId);
  return json<ConversationPage>(`${conversations}?${params}`, tenantId, { signal });
};

export const listMessages = (tenantId: string, id: string, offset = 0, signal?: AbortSignal) =>
  json<MessagePage>(`${assistantMessagesPath(id)}?limit=50&offset=${offset}`, tenantId, {
    signal,
    cache: 'no-store',
  });

export const completePendingQuestion = (tenantId: string, id: string) =>
  json<void>(assistantMessagesPath(id), tenantId, {
    method: 'POST',
    body: JSON.stringify({ complete_pending_question: true }),
  });

export const requestAssistantSteer = (tenantId: string, id: string) =>
  json<void>(`${assistantMessagesPath(id)}/steer`, tenantId, {
    method: 'POST',
    body: JSON.stringify({}),
  });

export const createConversation = (
  tenantId: string,
  query: string,
  signal?: AbortSignal,
  appId?: string,
  id?: string
) =>
  json<Conversation>(conversations, tenantId, {
    method: 'POST',
    body: JSON.stringify({ query, ...(appId ? { app_id: appId } : {}), ...(id ? { id } : {}) }),
    signal,
  });

export const renameConversation = (
  tenantId: string,
  id: string,
  title: string,
  signal?: AbortSignal
) =>
  json(conversationPath(id), tenantId, {
    method: 'PATCH',
    body: JSON.stringify({ title }),
    signal,
  });

export const deleteConversation = (tenantId: string, id: string, signal?: AbortSignal) =>
  json(conversationPath(id), tenantId, { method: 'DELETE', signal });

export const getFeedbackCategories = (tenantId: string, signal?: AbortSignal) =>
  json<string[]>('/app/fabrik/assistant-messages/feedback-categories', tenantId, { signal });

export const saveFeedback = (
  tenantId: string,
  id: string,
  messageId: string,
  body: { rating: string; category?: string; comment?: string },
  signal?: AbortSignal
) =>
  json(`${assistantMessagesPath(id)}/${messageId}/feedback`, tenantId, {
    method: 'PUT',
    body: JSON.stringify(body),
    signal,
  });

export const deleteFeedback = (
  tenantId: string,
  id: string,
  messageId: string,
  signal?: AbortSignal
) =>
  json(`${assistantMessagesPath(id)}/${messageId}/feedback`, tenantId, {
    method: 'DELETE',
    signal,
  });
